import { useState } from "react";
import {
  cancelCreditSubscription,
  changeCreditSubscriptionTier,
  fetchCreditCheckoutSession,
  fetchCreditTransactions,
  fetchPortalSessionUrl,
  previewCreditTierChange,
  useActiveSubscription,
  useMessagingCredits,
  usePostPurchaseBalancePoller,
} from "@/data/v2/client/manager/credits";
import { automationsKeys } from "@punchup/data/hooks";
import type { CreditMode, CreditTier } from "@punchup/data/models";
import type { CreditTransaction } from "@/data/v2/models/messaging-credits.types";
import { useQueryClient } from "@tanstack/react-query";

interface UseCreditModalStateArgs {
  comedianId: string;
  comedianSlug: string;
  comedianDisplayName: string;
}

export function useCreditModalState({
  comedianId,
  comedianDisplayName,
}: UseCreditModalStateArgs) {
  // Modal state
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditModalInitialTab, setCreditModalInitialTab] = useState<
    "details" | "history" | "buy-credits"
  >("buy-credits");

  // Post-purchase polling state
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [previousBalance, setPreviousBalance] = useState(0);

  const queryClient = useQueryClient();

  // Credit balance hook
  const { data: creditsData, isLoading: creditsLoading } =
    useMessagingCredits(comedianId);

  // Active subscription hook
  const { data: activeSubscription, isLoading: subscriptionLoading } =
    useActiveSubscription(comedianId);

  // Post-purchase polling hook
  usePostPurchaseBalancePoller(comedianId, {
    enabled: pollingEnabled,
    previousBalance,
    onBalanceUpdated: () => {
      setPollingEnabled(false);
      // Auto-close the credit modal after a brief delay so the user sees the success state
      setTimeout(() => {
        setCreditModalOpen(false);
      }, 500);
    },
  });

  const openCreditModal = (tab?: "details" | "history" | "buy-credits") => {
    setCreditModalInitialTab(tab ?? "buy-credits");
    setCreditModalOpen(true);
  };

  const handlePaymentComplete = () => {
    // Capture current balance before polling starts
    setPreviousBalance(creditsData?.totalBalance ?? 0);
    // Immediately invalidate stale cached balance before polling starts
    void queryClient.invalidateQueries({
      queryKey: automationsKeys.creditBalance(comedianId),
    });
    // Invalidate subscription cache so Details tab reflects new subscription
    void queryClient.invalidateQueries({
      queryKey: ["creditSubscription", comedianId],
    });
    // Enable polling to detect when the new credits arrive
    setPollingEnabled(true);
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    await cancelCreditSubscription(comedianId, subscriptionId);
    void queryClient.invalidateQueries({
      queryKey: ["creditSubscription", comedianId],
    });
    void queryClient.invalidateQueries({
      queryKey: automationsKeys.creditBalance(comedianId),
    });
  };

  const handleUpdatePaymentMethod = async (): Promise<string> => {
    return fetchPortalSessionUrl(comedianId);
  };

  const handleChangeTier = async (newTier: CreditTier) => {
    if (!activeSubscription?.subscriptionId) return;
    await changeCreditSubscriptionTier(
      comedianId,
      activeSubscription.subscriptionId,
      newTier
    );
    // Invalidate subscription cache so UI reflects new tier
    void queryClient.invalidateQueries({
      queryKey: ["creditSubscription", comedianId],
    });
    // Invalidate balance cache in case proration affects balance
    void queryClient.invalidateQueries({
      queryKey: automationsKeys.creditBalance(comedianId),
    });
  };

  const handlePreviewTierChange = async (
    newTier: CreditTier
  ): Promise<number> => {
    if (!activeSubscription?.subscriptionId) {
      throw new Error("No active subscription to preview a tier change for");
    }
    return previewCreditTierChange(
      comedianId,
      activeSubscription.subscriptionId,
      newTier
    );
  };

  const fetchCheckoutSession = (
    tier: CreditTier,
    mode: CreditMode
  ): Promise<{ clientSecret: string }> => {
    return fetchCreditCheckoutSession(
      comedianId,
      tier,
      mode,
      comedianDisplayName
    );
  };

  const fetchTransactions = (): Promise<CreditTransaction[]> => {
    return fetchCreditTransactions(comedianId);
  };

  return {
    // Balance data
    creditBalance: creditsData?.totalBalance ?? 0,
    creditsLoading,
    subscriptionLoading,
    isPolling: pollingEnabled,
    // Modal control
    creditModalOpen,
    setCreditModalOpen,
    creditModalInitialTab,
    openCreditModal,
    // CreditBalanceModal callback props
    creditsData,
    activeSubscription,
    handlePaymentComplete,
    handleCancelSubscription,
    handleUpdatePaymentMethod,
    handleChangeTier,
    handlePreviewTierChange,
    fetchCheckoutSession,
    fetchTransactions,
  };
}
